import { motion } from 'framer-motion';
import { Edit2 } from 'lucide-react';
import { cn } from '../lib/utils';

interface ReviewSectionProps {
  questionNumber: number;
  title: string;
  children: React.ReactNode;
  onEdit: () => void;
}

export function ReviewSection({
  questionNumber,
  title,
  children,
  onEdit,
}: ReviewSectionProps) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: questionNumber * 0.1 }}
      className="group bg-slate-900/50 backdrop-blur-sm rounded-xl p-5 border border-slate-700/50 hover:border-primary-500/30 transition-colors"
    >
      <div className="flex justify-between items-start mb-4">
        <div className="flex items-center gap-3">
          <span className="w-8 h-8 rounded-lg bg-primary-500/10 flex items-center justify-center text-primary-400 text-sm font-bold">
            {questionNumber}
          </span>
          <h3 className="font-semibold text-slate-200">{title}</h3>
        </div>
        <button
          onClick={onEdit}
          className="p-2 rounded-lg text-slate-400 hover:text-primary-400 hover:bg-primary-500/10 transition-all opacity-0 group-hover:opacity-100"
          aria-label={`Edit ${title}`}
        >
          <Edit2 className="w-4 h-4" />
        </button>
      </div>

      <div className="space-y-3 text-slate-300">{children}</div>
    </motion.div>
  );
}

interface ReviewFieldProps {
  label: string;
  value: string;
  className?: string;
}

export function ReviewField({ label, value, className }: ReviewFieldProps) {
  return (
    <div className={cn('space-y-1', className)}>
      <span className="text-xs text-slate-500 uppercase tracking-wide">
        {label}
      </span>
      <p className="text-slate-300 whitespace-pre-wrap">
        {value || <span className="text-slate-500 italic">Not provided</span>}
      </p>
    </div>
  );
}

interface ReviewListProps {
  items: string[];
  label?: string;
}

export function ReviewList({ items, label }: ReviewListProps) {
  return (
    <div className="space-y-2">
      {label && (
        <span className="text-xs text-slate-500 uppercase tracking-wide">
          {label}
        </span>
      )}
      <ul className="space-y-2">
        {items.map((item, index) => (
          <li key={index} className="flex gap-2">
            <span className="text-primary-500">•</span>
            <span className="text-slate-300">
              {item || (
                <span className="text-slate-500 italic">Not provided</span>
              )}
            </span>
          </li>
        ))}
      </ul>
    </div>
  );
}

interface ReviewObjectionProps {
  index: number;
  politeReason: string;
  realReason: string;
}

export function ReviewObjection({
  index,
  politeReason,
  realReason,
}: ReviewObjectionProps) {
  return (
    <div className="bg-slate-800/50 rounded-lg p-4 space-y-3">
      <span className="text-xs text-slate-500 font-medium">
        Objection #{index + 1}
      </span>
      <div className="grid gap-3 md:grid-cols-2">
        <div>
          <span className="text-xs text-slate-500">They say:</span>
          <p className="text-slate-300 text-sm">
            {politeReason || (
              <span className="text-slate-500 italic">Not provided</span>
            )}
          </p>
        </div>
        <div>
          <span className="text-xs text-slate-500">They really fear:</span>
          <p className="text-slate-300 text-sm">
            {realReason || (
              <span className="text-slate-500 italic">Not provided</span>
            )}
          </p>
        </div>
      </div>
    </div>
  );
}
